import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success(t('contact.form.success'));
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) { toast.error(t('contact.form.error')); }
    finally { setLoading(false); }
  };

  const pg = {
    nl: { title: 'Contact' },
    en: { title: 'Contact' },
    fr: { title: 'Contact' },
    de: { title: 'Kontakt' },
  }[language] || { title: 'Contact' };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="bg-[#0F172A] pt-28 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">{pg.title}</h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Info */}
            <div className="lg:col-span-2 space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-[#BE1622]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-4 w-4 text-[#BE1622]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1 text-sm">{t('contact.address')}</h3>
                  <p className="text-slate-500 text-sm">Elektronikalaan 1<br />2610 Antwerpen, Belgie</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-[#BE1622]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-4 w-4 text-[#BE1622]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1 text-sm">{t('contact.phone')}</h3>
                  <a href="tel:+32475223211" className="text-slate-500 hover:text-[#BE1622] transition-colors text-sm">+32 475 22 32 11</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-[#BE1622]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4 text-[#BE1622]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1 text-sm">{t('contact.email')}</h3>
                  <a href="mailto:info@alphaprofiles.com" className="text-slate-500 hover:text-[#BE1622] transition-colors text-sm">info@alphaprofiles.com</a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="border border-gray-200 p-8">
                {success ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-12 h-12 bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2">{t('contact.form.success')}</h3>
                    <button onClick={() => setSuccess(false)} className="text-[#BE1622] font-medium text-sm hover:underline">{t('contact.form.send')}</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h3 className="text-sm font-bold text-slate-900 mb-6">{t('contact.form.send')}</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder={`${t('contact.form.name')} *`}
                          className="w-full px-3 py-2.5 border border-gray-200 focus:border-[#BE1622] outline-none transition-all text-sm" data-testid="contact-name-input" />
                        <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder={`${t('contact.form.email')} *`}
                          className="w-full px-3 py-2.5 border border-gray-200 focus:border-[#BE1622] outline-none transition-all text-sm" data-testid="contact-email-input" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('contact.form.phone')}
                          className="w-full px-3 py-2.5 border border-gray-200 focus:border-[#BE1622] outline-none transition-all text-sm" data-testid="contact-phone-input" />
                        <input type="text" name="company" value={formData.company} onChange={handleChange} placeholder={t('contact.form.company')}
                          className="w-full px-3 py-2.5 border border-gray-200 focus:border-[#BE1622] outline-none transition-all text-sm" data-testid="contact-company-input" />
                      </div>
                      <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} placeholder={`${t('contact.form.message')} *`}
                        className="w-full px-3 py-2.5 border border-gray-200 focus:border-[#BE1622] outline-none transition-all resize-none text-sm" data-testid="contact-message-input" />
                      <button type="submit" disabled={loading}
                        className="w-full bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[11px] py-3.5 transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit-btn">
                        {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Send className="w-3.5 h-3.5" /> {t('contact.form.send')}</>}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
